/**
 * The refresh token lives in an httpOnly cookie that no script on the page can read
 * (ADR 0005). The page keeps only two hints, neither secret: that a session may exist (so a
 * first-time visitor is not sent to the API on every load) and whether the user chose
 * "Remember me" (so each refresh renews the cookie the same way). Storage can be unavailable
 * or throw (private mode, blocked site data); then the app asks the API anyway.
 */
const SIGNED_IN = 'bovid.signedIn';
const REMEMBER = 'bovid.remember';
/** Where panels before the cookie kept the token; moved to the cookie on the next refresh. */
const LEGACY_TOKEN = 'bovid.refreshToken';

function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function read(store: Storage | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) local()?.removeItem(key);
    else local()?.setItem(key, value);
  } catch {
    // Blocked storage: the next load asks the API.
  }
}

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export const SIGNED_IN_KEY = SIGNED_IN;

export const sessionHint = {
  /** A session may exist: signed in before, or a token from before the cookie. */
  exists(): boolean {
    return local() === null || read(local(), SIGNED_IN) === '1' || this.legacyToken() !== null;
  },

  remember(): boolean {
    return read(local(), REMEMBER) === '1';
  },

  save(remember: boolean): void {
    write(SIGNED_IN, '1');
    write(REMEMBER, remember ? '1' : null);
  },

  clear(): void {
    write(SIGNED_IN, null);
    write(REMEMBER, null);
    this.clearLegacyToken();
  },

  /** A token kept by a panel from before the cookie: in localStorage if remembered. */
  legacyToken(): { token: string; remember: boolean } | null {
    const remembered = read(local(), LEGACY_TOKEN);
    if (remembered) return { token: remembered, remember: true };
    const tabOnly = read(session(), LEGACY_TOKEN);
    return tabOnly ? { token: tabOnly, remember: false } : null;
  },

  clearLegacyToken(): void {
    try {
      local()?.removeItem(LEGACY_TOKEN);
      session()?.removeItem(LEGACY_TOKEN);
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  },
};
